<template>
  <div class="replace-panel">
    <div class="workspace-grid two-cols">
      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>选择上传文件</span></div>
        </template>

        <el-upload
          ref="uploadfiletest"
          class="upload-block"
          :auto-upload="false"
          :limit="1"
          :on-change="replaceFile"
          :on-exceed="replaceFileTest"
          :show-file-list="true"
          accept=".exe,.dll,.xlsx,.xls,.json,.txt,.bin,.zip,*.*"
        >
          <template #trigger>
            <el-button type="primary">选择文件</el-button>
          </template>
          <el-button class="ml-12" @click="clearReplaceFile">清空</el-button>
        </el-upload>

        <div class="info-block">
          <div class="info-row">
            <span class="info-label">当前文件</span>
            <span class="info-value">{{ oldFile ? oldFile.name : '未选择文件' }}</span>
          </div>
          <div v-if="oldFile" class="info-row">
            <span class="info-label">文件大小</span>
            <span class="info-value">{{ formatFileSize(oldFile.size || 0) }}</span>
          </div>
        </div>
      </el-card>

      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>目标路径</span></div>
        </template>

        <div class="path-box">
          <el-input
            v-model="targetReplaceFilePath"
            placeholder="请输入完整绝对路径；下载仅允许 C:\mazda 下的文件"
          />
          <div class="path-actions">
            <el-button @click="showReplaceFileDialog">修改路径</el-button>
            <el-button :loading="queryFileSizeLoading" @click="queryCurrentTargetFileSize">查看当前文件大小</el-button>
            <el-button type="success" plain :loading="downloadLoading" @click="downloadTargetFile">下载该路径文件</el-button>
          </div>
        </div>

        <div class="tips-box">
          <div class="tip-line">1. 目标路径必须是 Windows 完整绝对路径。</div>
          <div class="tip-line">2. 目标文件名必须与上传文件名一致。</div>
          <div class="tip-line">3. 后端会先写入临时文件，再替换目标文件，避免写坏原文件。</div>
          <div class="tip-line">4. 上传替换路径不限制；但查看大小和下载只允许 C:\mazda 目录下的文件。</div>
        </div>

        <div v-if="targetFileSizeQueried" class="info-block">
          <div class="info-row">
            <span class="info-label">目标文件状态</span>
            <span class="info-value">{{ targetFileExists ? '存在' : '不存在' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">当前文件大小</span>
            <span class="info-value">{{ targetFileExists ? formatFileSize(targetFileSizeBytes) : '-' }}</span>
          </div>
          <div v-if="targetFileQueryMessage" class="info-row">
            <span class="info-label">查询结果</span>
            <span class="info-value">{{ targetFileQueryMessage }}</span>
          </div>
        </div>

        <div class="action-row">
          <el-button type="primary" :loading="loading" @click="submitUpload">开始替换</el-button>
          <el-button @click="clearReplaceFile">取消</el-button>
        </div>
      </el-card>
    </div>

    <el-dialog v-model="dialogExchangeFileName" title="修改目标文件路径" width="520px">
      <el-input v-model="editPath" placeholder="请输入新的完整绝对路径" />
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="cancelInputReplacePath">取消</el-button>
          <el-button type="primary" @click="confirmReplaceFile">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { useMazdaReplace } from '@/composables/mazda/useMazdaReplace';

const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});

const {
  targetReplaceFilePath,
  editPath,
  dialogExchangeFileName,
  uploadfiletest,
  oldFile,
  loading,
  queryFileSizeLoading,
  downloadLoading,
  targetFileSizeQueried,
  targetFileExists,
  targetFileSizeBytes,
  targetFileQueryMessage,
  formatFileSize,
  replaceFile,
  replaceFileTest,
  clearReplaceFile,
  showReplaceFileDialog,
  confirmReplaceFile,
  cancelInputReplacePath,
  queryCurrentTargetFileSize,
  downloadTargetFile,
  submitUpload,
} = useMazdaReplace(props.mazdaPost);
</script>

<style scoped>
.workspace-grid.two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(340px, 1fr));
  gap: 22px;
}

.workspace-card,
.ab-list-card,
.query-card {
  border-radius: 24px;
  border: 1px solid rgba(225, 232, 242, 0.95);
  box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07);
}

.workspace-card :deep(.el-card__body),
.ab-list-card :deep(.el-card__body),
.query-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 800;
  color: #1f2d3d;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-block {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 6px 0 4px;
}

.upload-block :deep(.el-button),
.header-actions :deep(.el-button),
.path-actions :deep(.el-button),
.action-row :deep(.el-button),
.toolbar-actions :deep(.el-button),
.dialog-footer :deep(.el-button) {
  min-width: 96px;
}

.ml-12 {
  margin-left: 12px;
}

.info-block {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
}

.info-row,
.result-row {
  display: flex;
  gap: 12px;
  line-height: 1.9;
}

.info-label,
.result-row span:first-child {
  width: 90px;
  color: #8a94a6;
  flex-shrink: 0;
}

.info-value,
.result-row strong {
  color: #2f3441;
  word-break: break-all;
}

.path-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.path-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.tips-box {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fffaf0, #fff7e6);
  border: 1px solid #ffe4ad;
}

.tip-line {
  line-height: 1.9;
  color: #7a5600;
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 22px;
  flex-wrap: wrap;
}

.result-panel {
  min-height: 150px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff, #f4f8ff);
  border: 1px solid #e8f1ff;
}

.empty-result {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  border-radius: 16px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

@media (max-width: 980px) {
  .workspace-grid.two-cols {
    grid-template-columns: 1fr;
  }

  .path-actions,
  .action-row {
    justify-content: flex-start;
  }
}
</style>
